import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FileText,
  ChevronLeft,
  ChevronRight,
  Search,
  Building2,
  Filter,
  Layers,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  BarChart3,
  Plus,
  Users,
  Clock,
  BookOpen,
  DoorOpen,
  GraduationCap,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { RoomOccupancyButton } from '../components/room-occupancy/RoomOccupancyButton';
import { Permission } from '@/types/global.types';
import { fetchFilieresGrouped, getSemestersByFiliere, semesterOptionId, optionsForNiveau } from '@/services/filieresService';
import { getAvailableCampuses, getGroupesByFiliere } from '@/services/groupsService';
import { fetchAcademicYears, AcademicYear } from '@/services/academicPlanningService';
import type { FiliereWithOptions, ModuleElement, SemesterWithModules } from '@/types/filiere.types';
import type { GroupeWithCounts } from '@/types/group.types';
import examScheduleService, {
  ExamCalendarData,
  ExamCalendarEntry,
  ExamPeriods,
} from '../services/examScheduleService';
import ExamEntryModal from '../components/exam-scheduling/ExamEntryModal';
import { ExamSchedulingProgressPanel } from '../components/exam-scheduling/ExamSchedulingProgressPanel';

interface Campus {
  id: string;
  name: string;
  sigle: string | null;
  isMainCampus: boolean;
}

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Helper to determine current semester based on academic year dates
function getCurrentSemester(academicYear: AcademicYear | null): 1 | 2 {
  if (!academicYear) return 1;
  const now = new Date();
  if (academicYear.semester2StartDate) {
    const semester2Start = new Date(academicYear.semester2StartDate);
    if (now >= semester2Start) return 2;
    return 1;
  }
  if (academicYear.semester1EndDate) {
    const semester1End = new Date(academicYear.semester1EndDate);
    if (now > semester1End) return 2;
    return 1;
  }
  const month = now.getMonth();
  return month >= 1 && month <= 6 ? 2 : 1;
}

const ExamSchedulingPage: React.FC = () => {
  const { tokens } = useAuthStore();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.EXAM_SCHEDULE_MANAGE);

  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Data State
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<AcademicYear | null>(null);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [selectedCampusId, setSelectedCampusId] = useState<string | null>(null);
  const [filieres, setFilieres] = useState<FiliereWithOptions[]>([]);
  const [selectedFiliereId, setSelectedFiliereId] = useState<string | null>(null);
  // Option of the filière at the selected niveau (specialisation years only)
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [filiereSemesters, setFiliereSemesters] = useState<SemesterWithModules[]>([]);
  const [niveaux, setNiveaux] = useState<number[]>([]);
  const [selectedNiveau, setSelectedNiveau] = useState<number | null>(null);
  const [groupes, setGroupes] = useState<GroupeWithCounts[]>([]);
  const [selectedGroupeId, setSelectedGroupeId] = useState<string | null>(null);
  const [selectedSemester, setSelectedSemester] = useState<1 | 2>(1);

  // Matières (module elements) for the selected group/semester
  const [matieres, setMatieres] = useState<ModuleElement[]>([]);
  const [loadingMatieres, setLoadingMatieres] = useState(false);

  // Calendar state
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [calendarData, setCalendarData] = useState<ExamCalendarData | null>(null);
  const [examPeriods, setExamPeriods] = useState<ExamPeriods | null>(null);

  // Modals
  const [showExamModal, setShowExamModal] = useState(false);
  const [showProgressPanel, setShowProgressPanel] = useState(false);
  const [selectedMatiereForExam, setSelectedMatiereForExam] = useState<ModuleElement | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [examToEdit, setExamToEdit] = useState<ExamCalendarEntry | null>(null);

  // Weekly view state
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Monday start
    return new Date(today.setDate(diff));
  });

  // Time slot selection for drag functionality
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(null);
  const [selectedEndTime, setSelectedEndTime] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartDate, setDragStartDate] = useState<string | null>(null);
  const [dragStartHour, setDragStartHour] = useState<number | null>(null);
  const [dragCurrentHour, setDragCurrentHour] = useState<number | null>(null);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    if (!tokens?.accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const [yearsData, campusData, filieresData] = await Promise.all([
        fetchAcademicYears(tokens.accessToken),
        getAvailableCampuses(),
        fetchFilieresGrouped(true),
      ]);

      setAcademicYears(yearsData);
      setCampuses(campusData.campuses);
      setHasMultipleCampuses(campusData.hasMultipleCampuses);
      setFilieres(filieresData);

      // Set current academic year
      const currentYear = yearsData.find((y: AcademicYear) => y.isCurrent);
      if (currentYear) {
        setSelectedAcademicYear(currentYear);
        setSelectedSemester(getCurrentSemester(currentYear));
      }

      // Set default campus
      if (campusData.hasMultipleCampuses && campusData.campuses.length > 0) {
        const mainCampus = campusData.campuses.find((c: Campus) => c.isMainCampus);
        setSelectedCampusId(mainCampus?.id || campusData.campuses[0].id);
      } else if (campusData.campuses.length === 1) {
        setSelectedCampusId(campusData.campuses[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  }, [tokens?.accessToken]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Update niveaux when filière changes
  useEffect(() => {
    if (selectedFiliereId) {
      const filiere = filieres.find(f => f.id === selectedFiliereId);
      if (filiere) {
        const niveauxArray = Array.from({ length: filiere.nombreAnnees }, (_, i) => i + 1);
        setNiveaux(niveauxArray);
        setSelectedNiveau(null);
        setSelectedOptionId(null);
        setSelectedGroupeId(null);
      }
    } else {
      setNiveaux([]);
      setSelectedNiveau(null);
      setSelectedOptionId(null);
      setSelectedGroupeId(null);
    }
  }, [selectedFiliereId, filieres]);

  // Load the filière's semesters once (drives both matières and option detection)
  useEffect(() => {
    if (!selectedFiliereId) {
      setFiliereSemesters([]);
      return;
    }
    getSemestersByFiliere(selectedFiliereId)
      .then(setFiliereSemesters)
      .catch((err) => { console.error('Failed to load semesters:', err); setFiliereSemesters([]); });
  }, [selectedFiliereId]);

  // Options applicable at the selected niveau (specialisation years)
  const selectedFiliere = filieres.find((f) => f.id === selectedFiliereId);
  const availableOptions = useMemo(() => {
    if (!selectedFiliere || !selectedNiveau) return [];
    return optionsForNiveau(selectedFiliere, filiereSemesters, selectedNiveau);
  }, [selectedFiliere, filiereSemesters, selectedNiveau]);

  // Load groupes when filière, option, niveau, or campus changes.
  // On specialisation years the groups belong to the OPTION filière.
  useEffect(() => {
    const loadGroupes = async () => {
      if (!selectedFiliereId || !selectedAcademicYear) {
        setGroupes([]);
        return;
      }
      if (availableOptions.length > 0 && !selectedOptionId) {
        setGroupes([]); // an option must be chosen first at this niveau
        return;
      }

      try {
        const data = await getGroupesByFiliere(
          selectedOptionId || selectedFiliereId,
          selectedAcademicYear.id,
          selectedCampusId || undefined
        );
        const filtered = selectedNiveau
          ? data.filter(g => g.niveau === selectedNiveau)
          : data;
        setGroupes(filtered);
      } catch (err) {
        console.error('Failed to load groupes:', err);
        setGroupes([]);
      }
    };

    loadGroupes();
  }, [selectedFiliereId, selectedOptionId, availableOptions, selectedAcademicYear, selectedCampusId, selectedNiveau]);

  // Load matières when groupe and semester change
  useEffect(() => {
    const loadMatieres = async () => {
      if (!selectedFiliereId || !selectedGroupeId) {
        setMatieres([]);
        return;
      }

      setLoadingMatieres(true);
      try {
        const groupe = groupes.find(g => g.id === selectedGroupeId);
        if (!groupe) return;

        // On specialisation years the niveau's semester exists once per option
        const semesterNumber = (groupe.niveau - 1) * 2 + selectedSemester;
        const candidates = filiereSemesters.filter(s => s.number === semesterNumber);
        const matchingSemester =
          candidates.find(s => semesterOptionId(s) === (selectedOptionId || null))
          || candidates.find(s => !semesterOptionId(s))
          || candidates[0];

        if (matchingSemester) {
          const elements: ModuleElement[] = [];
          matchingSemester.modules.forEach(module => {
            elements.push(...module.elements);
          });
          setMatieres(elements);
        } else {
          setMatieres([]);
        }
      } catch (err) {
        console.error('Failed to load matières:', err);
        setMatieres([]);
      } finally {
        setLoadingMatieres(false);
      }
    };

    loadMatieres();
  }, [selectedFiliereId, selectedOptionId, selectedGroupeId, selectedSemester, groupes, filiereSemesters]);

  // Load exam periods
  useEffect(() => {
    const loadExamPeriods = async () => {
      if (!selectedAcademicYear) return;
      try {
        // Campus-specific periods when the exam config is in per-campus mode
        const periods = await examScheduleService.getExamPeriods(selectedAcademicYear.id, selectedCampusId || undefined);
        setExamPeriods(periods);
      } catch (err) {
        console.error('Error loading exam periods:', err);
      }
    };
    loadExamPeriods();
  }, [selectedAcademicYear, selectedCampusId]);

  // The visible week can straddle two months (e.g. 29 déc - 4 jan): fetch both
  // months and merge, otherwise exams near a month boundary vanish from the grid
  const fetchWeekCalendar = async (): Promise<ExamCalendarData | null> => {
    if (!selectedAcademicYear || !selectedCampusId) return null;
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const months: { month: number; year: number }[] = [
      { month: currentWeekStart.getMonth() + 1, year: currentWeekStart.getFullYear() },
    ];
    if (weekEnd.getMonth() !== currentWeekStart.getMonth()) {
      months.push({ month: weekEnd.getMonth() + 1, year: weekEnd.getFullYear() });
    }
    const results = await Promise.all(
      months.map(({ month, year }) =>
        examScheduleService.getCalendar(
          selectedAcademicYear.id,
          selectedCampusId,
          month,
          year,
          // Option exams are stored under the OPTION filière id
          selectedOptionId || selectedFiliereId || undefined,
          selectedGroupeId || undefined
        )
      )
    );
    return { ...results[0], exams: Object.assign({}, ...results.map((r) => r.exams)) };
  };

  // Load calendar data
  useEffect(() => {
    const loadCalendar = async () => {
      if (!selectedAcademicYear || !selectedCampusId) {
        setCalendarData(null);
        return;
      }
      try {
        setCalendarData(await fetchWeekCalendar());
      } catch (err) {
        console.error('Error loading calendar:', err);
      }
    };
    loadCalendar();
  }, [selectedAcademicYear, selectedCampusId, selectedFiliereId, selectedOptionId, selectedGroupeId, currentWeekStart]);

  // Helper to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (year: number, month: number, day: number): string => {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Check if date is in exam period
  const isInExamPeriod = (dateStr: string): { inPeriod: boolean; periodName: string | null; periodColor: string } => {
    if (!examPeriods) return { inPeriod: false, periodName: null, periodColor: '' };
    const date = new Date(dateStr);

    const checkPeriod = (start: string | null, end: string | null, name: string, color: string) => {
      if (start && end) {
        const s = new Date(start);
        const e = new Date(end);
        if (date >= s && date <= e) return { inPeriod: true, periodName: name, periodColor: color };
      }
      return null;
    };

    return (
      checkPeriod(examPeriods.period1?.start ?? null, examPeriods.period1?.end ?? null, 'Examens S1', 'bg-blue-50') ||
      checkPeriod(examPeriods.period2?.start ?? null, examPeriods.period2?.end ?? null, 'Examens S2', 'bg-green-50') ||
      { inPeriod: false, periodName: null, periodColor: '' }
    );
  };

  const handleToday = () => {
    const today = new Date();
    // Update week start for weekly view
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(new Date().setDate(diff)));
  };

  // Weekly view navigation
  const handlePrevWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const handleNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  // Calculate week days for weekly view
  const weekDays = useMemo(() => {
    const days: { date: string; dayNum: number; dayName: string; isToday: boolean; exams: ExamCalendarEntry[] }[] = [];
    const weekDayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    const today = new Date().toISOString().split('T')[0];

    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      const dateStr = formatDateString(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const exams = calendarData?.exams[dateStr] || [];

      days.push({
        date: dateStr,
        dayNum: date.getDate(),
        dayName: weekDayNames[i],
        isToday: dateStr === today,
        exams,
      });
    }
    return days;
  }, [currentWeekStart, calendarData]);

  // Get week range label
  const weekRangeLabel = useMemo(() => {
    const endOfWeek = new Date(currentWeekStart);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    const startMonth = MONTHS[currentWeekStart.getMonth()];
    const endMonth = MONTHS[endOfWeek.getMonth()];

    if (currentWeekStart.getMonth() === endOfWeek.getMonth()) {
      return `${currentWeekStart.getDate()} - ${endOfWeek.getDate()} ${startMonth} ${currentWeekStart.getFullYear()}`;
    }
    return `${currentWeekStart.getDate()} ${startMonth} - ${endOfWeek.getDate()} ${endMonth} ${endOfWeek.getFullYear()}`;
  }, [currentWeekStart]);

  // Time slot drag handlers for weekly view
  const handleTimeSlotMouseDown = (date: string, hour: number) => {
    if (!canManage) return;
    setIsDragging(true);
    setDragStartDate(date);
    setDragStartHour(hour);
    setDragCurrentHour(hour);
    setSelectedDate(date);
  };

  const handleTimeSlotMouseEnter = (hour: number) => {
    if (isDragging && dragStartHour !== null) {
      setDragCurrentHour(hour);
    }
  };

  const handleTimeSlotMouseUp = () => {
    if (!isDragging || dragStartHour === null || dragCurrentHour === null || !dragStartDate) {
      setIsDragging(false);
      return;
    }

    const startHour = Math.min(dragStartHour, dragCurrentHour);
    const endHour = Math.max(dragStartHour, dragCurrentHour) + 1; // +1 because we want to include the end hour

    const startTime = `${startHour.toString().padStart(2, '0')}:00`;
    const endTime = `${endHour.toString().padStart(2, '0')}:00`;

    setSelectedStartTime(startTime);
    setSelectedEndTime(endTime);
    setSelectedDate(dragStartDate);
    setSelectedMatiereForExam(null);
    setShowExamModal(true);

    // Reset drag state
    setIsDragging(false);
    setDragStartDate(null);
    setDragStartHour(null);
    setDragCurrentHour(null);
  };

  // Cancel drag on mouse leave from the grid
  const handleGridMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setDragStartDate(null);
      setDragStartHour(null);
      setDragCurrentHour(null);
    }
  };

  // Check if a time slot is in the drag selection range
  const isTimeSlotInDragRange = (date: string, hour: number) => {
    if (!isDragging || dragStartDate !== date || dragStartHour === null || dragCurrentHour === null) {
      return false;
    }
    const minHour = Math.min(dragStartHour, dragCurrentHour);
    const maxHour = Math.max(dragStartHour, dragCurrentHour);
    return hour >= minHour && hour <= maxHour;
  };

  const handleAddExam = () => {
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setEditMode(false);
    setExamToEdit(null);
    setShowExamModal(true);
  };

  const handleAddExamForMatiere = (matiere: ModuleElement) => {
    setSelectedMatiereForExam(matiere);
    setEditMode(false);
    setExamToEdit(null);
    setShowExamModal(true);
  };

  // Handler to edit an existing exam
  const handleEditExam = (exam: ExamCalendarEntry, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering time slot selection
    if (!canManage) return;

    setEditMode(true);
    setExamToEdit(exam);
    setSelectedDate(exam.examDate);
    setSelectedMatiereForExam(null);
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setShowExamModal(true);
  };

  const handleExamCreated = () => {
    setShowExamModal(false);
    setEditMode(false);
    setExamToEdit(null);
    setSuccess(editMode ? 'Examen modifié avec succès' : 'Examen créé avec succès');
    // Reload calendar
    fetchWeekCalendar()
      .then((data) => data && setCalendarData(data))
      .catch(console.error);
  };

  const handleExamDeleted = () => {
    setSuccess('Examen supprimé avec succès');
  };

  // Calculate scheduled exams per matière for progress tracking
  const scheduledExamsMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!calendarData) return map;

    Object.values(calendarData.exams).forEach(dayExams => {
      dayExams.forEach(exam => {
        if (!exam.isRattrapage) {
          const current = map.get(exam.moduleElement.id) || 0;
          map.set(exam.moduleElement.id, current + 1);
        }
      });
    });

    return map;
  }, [calendarData]);

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  const getNiveauLabel = (niveau: number): string => {
    const suffixes: Record<number, string> = {
      1: '1ère Année',
      2: '2ème Année',
      3: '3ème Année',
      4: '4ème Année',
      5: '5ème Année',
    };
    return suffixes[niveau] || `${niveau}ème Année`;
  };

  // Filter filières by search
  const filteredFilieres = filieres.filter((f) => {
    const query = searchQuery.toLowerCase();
    return (
      f.filiere.toLowerCase().includes(query) ||
      f.codeFiliere.toLowerCase().includes(query) ||
      f.diplome.toLowerCase().includes(query)
    );
  });

  // Get selected groupe
  const selectedGroupe = groupes.find((g) => g.id === selectedGroupeId);

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header - Compact */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-50 rounded-lg">
              <FileText className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900">
                Planification des Examens
              </h1>
              <p className="text-xs text-gray-500">
                Planifiez et gérez les examens par groupe
              </p>
            </div>
          </div>

          {/* Top Selectors */}
          <div className="flex items-center gap-3">
            <RoomOccupancyButton
              campuses={campuses}
              campusId={selectedCampusId}
              date={currentWeekStart}
              academicYearId={selectedAcademicYear?.id}
            />

            {/* Campus Selector */}
            {hasMultipleCampuses && campuses.length > 1 && (
              <div className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-gray-500" />
                <select
                  value={selectedCampusId || ''}
                  onChange={(e) => setSelectedCampusId(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 bg-purple-50"
                >
                  {campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.sigle || campus.name} {campus.isMainCampus ? '(Principal)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Academic Year Selector */}
            <select
              value={selectedAcademicYear?.id || ''}
              onChange={(e) => {
                const year = academicYears.find(y => y.id === e.target.value);
                setSelectedAcademicYear(year || null);
                if (year) {
                  setSelectedSemester(getCurrentSemester(year));
                }
              }}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {academicYears.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name} {year.isCurrent ? '(Courante)' : ''}
                </option>
              ))}
            </select>

            {/* Semester Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setSelectedSemester(1)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  selectedSemester === 1
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                S1
              </button>
              <button
                onClick={() => setSelectedSemester(2)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  selectedSemester === 2
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                S2
              </button>
            </div>

            {/* Add Exam Button */}
            {canManage && (
              <button
                onClick={handleAddExam}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Ajouter un examen
              </button>
            )}

            {/* Progress Panel Toggle */}
            <button
              onClick={() => setShowProgressPanel(!showProgressPanel)}
              className={`p-1.5 rounded-lg transition-colors ${
                showProgressPanel
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
              }`}
              title={showProgressPanel ? 'Masquer la progression' : 'Afficher la progression'}
            >
              <BarChart3 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Alerts - Compact */}
      {error && (
        <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-xs flex-shrink-0">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {success && (
        <div className="mx-4 mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-xs flex-shrink-0">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Main Content - Fills remaining space */}
      <div className="flex-1 flex overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="flex-1 p-3 overflow-hidden">
            <div className="grid grid-cols-12 gap-3 h-full">
              {/* Left Sidebar - Filters and Matières */}
              <aside className="col-span-3 flex flex-col gap-2 overflow-hidden">
                {/* Filière Selection */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col flex-shrink-0" style={{ maxHeight: selectedGroupeId ? '45%' : '70%' }}>
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                      <Filter className="h-3.5 w-3.5" />
                      Sélection du groupe
                    </div>
                  </div>

                  {/* Search */}
                  <div className="p-2 border-b border-gray-100 flex-shrink-0">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Rechercher une filière..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* Filières List */}
                  <div className="flex-1 overflow-y-auto min-h-0">
                    {filteredFilieres.length === 0 ? (
                      <div className="p-3 text-center text-gray-500 text-xs">
                        Aucune filière trouvée
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {filteredFilieres.map((filiere) => (
                          <button
                            key={filiere.id}
                            onClick={() => setSelectedFiliereId(filiere.id)}
                            className={`w-full px-3 py-2 text-left transition-colors ${
                              selectedFiliereId === filiere.id
                                ? 'bg-purple-50 border-l-3 border-purple-500'
                                : 'hover:bg-gray-50 border-l-3 border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <Layers className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 leading-snug break-words" title={filiere.filiere}>
                                  {filiere.filiere}
                                </p>
                                <p className="text-[10px] text-gray-500 leading-snug break-words">
                                  {filiere.codeFiliere}{filiere.diplome ? ` • ${filiere.diplome}` : ''}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Niveau and Groupe Selection */}
                  {selectedFiliereId && (
                    <div className="p-2 space-y-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                      {/* Niveau Selector */}
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                          Niveau
                        </label>
                        <select
                          value={selectedNiveau || ''}
                          onChange={(e) => {
                            setSelectedNiveau(e.target.value ? parseInt(e.target.value) : null);
                            setSelectedOptionId(null);
                            setSelectedGroupeId(null);
                          }}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Tous les niveaux</option>
                          {niveaux.map((niveau) => (
                            <option key={niveau} value={niveau}>
                              {getNiveauLabel(niveau)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Option Selector — only when the selected niveau has options */}
                      {availableOptions.length > 0 && (
                        <div>
                          <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                            Option
                          </label>
                          <select
                            value={selectedOptionId || ''}
                            onChange={(e) => {
                              setSelectedOptionId(e.target.value || null);
                              setSelectedGroupeId(null);
                            }}
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Sélectionner une option</option>
                            {availableOptions.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.optionName || o.filiere} ({o.codeFiliere})
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Groupe Selector */}
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                          Groupe
                        </label>
                        <select
                          value={selectedGroupeId || ''}
                          onChange={(e) => setSelectedGroupeId(e.target.value || null)}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Sélectionner un groupe</option>
                          {groupes.map((groupe) => (
                            <option key={groupe.id} value={groupe.id}>
                              {groupe.name} ({groupe.studentCount || 0} étudiants)
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Matières Panel */}
                {selectedGroupeId && (
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 flex flex-col min-h-0">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                          <BookOpen className="h-3.5 w-3.5" />
                          Matières - S{selectedGroupe ? (selectedGroupe.niveau - 1) * 2 + selectedSemester : selectedSemester}
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          Cliquez pour planifier un examen
                        </p>
                      </div>
                      {canManage && (
                        <button
                          onClick={handleAddExam}
                          className="p-1.5 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
                          title="Ajouter un examen"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0">
                      {loadingMatieres ? (
                        <div className="p-3 flex justify-center">
                          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                        </div>
                      ) : matieres.length === 0 ? (
                        <div className="p-3 text-center text-gray-500 text-xs">
                          Aucune matière pour ce semestre
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {matieres.map((matiere) => {
                            const hasExam = scheduledExamsMap.has(matiere.id);
                            const examCount = scheduledExamsMap.get(matiere.id) || 0;

                            return (
                              <button
                                key={matiere.id}
                                onClick={() => handleAddExamForMatiere(matiere)}
                                disabled={!canManage}
                                draggable={canManage}
                                onDragStart={(e) => {
                                  e.dataTransfer.effectAllowed = 'copy';
                                  e.dataTransfer.setData('matiere', JSON.stringify(matiere));
                                  e.currentTarget.style.opacity = '0.5';
                                }}
                                onDragEnd={(e) => {
                                  e.currentTarget.style.opacity = '1';
                                }}
                                className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-all text-left ${!canManage ? 'cursor-not-allowed opacity-60' : 'cursor-move'}`}
                                title={canManage ? 'Cliquez ou glissez pour planifier un examen' : 'Vous n\'avez pas les permissions'}
                              >
                                {/* Status indicator */}
                                <div
                                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    hasExam ? 'bg-green-500' : 'bg-gray-300'
                                  }`}
                                  title={hasExam ? `${examCount} examen(s) planifié(s)` : 'Non planifié'}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-900 leading-snug line-clamp-2" title={matiere.name}>
                                    {matiere.name}
                                  </p>
                                  <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                    <span>Coef. {matiere.weight}%</span>
                                    {hasExam && (
                                      <span className="text-green-600">
                                        {examCount} examen{examCount > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick Tips / Exam Periods */}
                {!selectedGroupeId ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex-shrink-0">
                    <h4 className="text-xs font-medium text-purple-800 mb-1.5">
                      Comment utiliser
                    </h4>
                    <ul className="text-[10px] text-purple-700 space-y-0.5">
                      <li>1. Sélectionnez une filière</li>
                      <li>2. Choisissez le niveau et le groupe</li>
                      <li>3. Cliquez sur une matière pour planifier</li>
                      <li>4. Ou cliquez sur une date du calendrier</li>
                    </ul>
                  </div>
                ) : examPeriods && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3 flex-shrink-0">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">
                      Périodes d'examens
                    </h4>
                    <div className="space-y-1.5">
                      {examPeriods.period1?.start && selectedSemester === 1 && (
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <p className="text-[10px] font-medium text-blue-800">Examens S1</p>
                          <p className="text-[10px] text-blue-600">
                            {new Date(examPeriods.period1.start).toLocaleDateString('fr-FR')} -{' '}
                            {new Date(examPeriods.period1.end!).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      )}
                      {examPeriods.period2?.start && selectedSemester === 2 && (
                        <div className="p-2 bg-green-50 rounded-lg">
                          <p className="text-[10px] font-medium text-green-800">Examens S2</p>
                          <p className="text-[10px] text-green-600">
                            {new Date(examPeriods.period2.start).toLocaleDateString('fr-FR')} -{' '}
                            {new Date(examPeriods.period2.end!).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </aside>

              {/* Main Content - Calendar Grid */}
              <main className="col-span-9 flex flex-col overflow-hidden">
                {/* Navigation - Weekly */}
                <div className="flex items-center justify-between mb-2 bg-white rounded-lg border border-gray-200 p-2 flex-shrink-0">
                  <button
                    onClick={handlePrevWeek}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-900">
                      {weekRangeLabel}
                    </span>
                    <button
                      onClick={handleToday}
                      className="px-2 py-1 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    >
                      Aujourd'hui
                    </button>
                  </div>

                  <button
                    onClick={handleNextWeek}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>

                {/* Calendar Grid */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
                  {!selectedGroupeId ? (
                    <div className="p-8 text-center h-full flex flex-col items-center justify-center">
                      <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <Users className="h-6 w-6 text-gray-400" />
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        Sélectionnez un groupe
                      </h3>
                      <p className="text-xs text-gray-500">
                        Choisissez une filière et un groupe pour voir et planifier ses examens
                      </p>
                    </div>
                  ) : (
                    /* WEEKLY VIEW WITH TIME GRID */
                    <div className="flex-1 flex flex-col overflow-hidden">
                      {/* Week header with days */}
                      <div className="flex border-b border-gray-200 flex-shrink-0">
                        {/* Time column header */}
                        <div className="w-14 flex-shrink-0 bg-gray-50 border-r border-gray-200" />
                        {/* Day columns */}
                        <div className="flex-1 grid grid-cols-7">
                          {weekDays.map((day) => {
                            const { inPeriod } = isInExamPeriod(day.date);
                            const isWeekend = day.dayName === 'Sam' || day.dayName === 'Dim';
                            return (
                              <div
                                key={day.date}
                                className={`text-center py-2 border-r border-gray-100 last:border-r-0 ${
                                  isWeekend ? 'bg-gray-100' : inPeriod ? 'bg-purple-50' : 'bg-gray-50'
                                }`}
                              >
                                <p className={`text-[10px] font-medium ${isWeekend ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {day.dayName}
                                </p>
                                <p className={`text-sm font-semibold ${
                                  day.isToday
                                    ? 'bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center mx-auto'
                                    : isWeekend ? 'text-gray-400' : 'text-gray-900'
                                }`}>
                                  {day.dayNum}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Time grid with slots */}
                      <div
                        className="flex-1 flex overflow-y-auto select-none"
                        onMouseLeave={handleGridMouseLeave}
                        onMouseUp={handleTimeSlotMouseUp}
                      >
                        {/* Time labels column */}
                        <div className="w-14 flex-shrink-0 bg-gray-50 border-r border-gray-200">
                          {Array.from({ length: 13 }, (_, i) => i + 8).map((hour) => (
                            <div
                              key={hour}
                              className="h-12 border-b border-gray-100 flex items-start justify-end pr-2 pt-0.5"
                            >
                              <span className="text-[10px] text-gray-500 font-medium">
                                {hour.toString().padStart(2, '0')}:00
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Day columns with time slots */}
                        <div className="flex-1 grid grid-cols-7 relative">
                          {weekDays.map((day) => {
                            const { inPeriod } = isInExamPeriod(day.date);
                            const isSelected = selectedDate === day.date;
                            const isWeekend = day.dayName === 'Sam' || day.dayName === 'Dim';

                            // Calculate exam positions based on time
                            const getExamPosition = (startTime: string, endTime: string) => {
                              const [startHour, startMin] = startTime.split(':').map(Number);
                              const [endHour, endMin] = endTime.split(':').map(Number);
                              const startMinutes = (startHour - 8) * 60 + startMin;
                              const endMinutes = (endHour - 8) * 60 + endMin;
                              const top = (startMinutes / 60) * 48; // 48px per hour (h-12 = 48px)
                              const height = ((endMinutes - startMinutes) / 60) * 48;
                              return { top: Math.max(0, top), height: Math.max(24, height) };
                            };

                            return (
                              <div
                                key={day.date}
                                className={`relative border-r border-gray-100 last:border-r-0 ${
                                  isWeekend ? 'bg-gray-50' : ''
                                } ${isSelected && !isDragging ? 'bg-purple-50' : ''} ${
                                  inPeriod && !isSelected && !isWeekend ? 'bg-purple-50/30' : ''
                                }`}
                              >
                                {/* Hour grid lines - clickable and draggable */}
                                {Array.from({ length: 13 }, (_, i) => i + 8).map((hour) => {
                                  const isInDragRange = isTimeSlotInDragRange(day.date, hour);
                                  return (
                                    <div
                                      key={hour}
                                      onMouseDown={() => !isWeekend && handleTimeSlotMouseDown(day.date, hour)}
                                      onMouseEnter={() => handleTimeSlotMouseEnter(hour)}
                                      onDragOver={(e) => {
                                        if (!isWeekend && canManage) {
                                          e.preventDefault();
                                          e.currentTarget.classList.add('bg-purple-200');
                                        }
                                      }}
                                      onDragLeave={(e) => {
                                        e.currentTarget.classList.remove('bg-purple-200');
                                      }}
                                      onDrop={(e) => {
                                        if (!isWeekend && canManage) {
                                          e.preventDefault();
                                          e.currentTarget.classList.remove('bg-purple-200');
                                          try {
                                            const matiereData = e.dataTransfer.getData('matiere');
                                            if (matiereData) {
                                              const matiere = JSON.parse(matiereData);
                                              // Set selected date and time
                                              setSelectedDate(day.date);

                                              // Check if there's an active time drag selection
                                              let startHour = hour;
                                              let endHour = hour + 2; // default 2 hours

                                              if (isDragging && dragStartDate === day.date && dragStartHour !== null && dragCurrentHour !== null) {
                                                // Use the dragged time range
                                                startHour = Math.min(dragStartHour, dragCurrentHour);
                                                endHour = Math.max(dragStartHour, dragCurrentHour) + 1;
                                                // Reset drag state
                                                setIsDragging(false);
                                                setDragStartDate(null);
                                                setDragStartHour(null);
                                                setDragCurrentHour(null);
                                              }

                                              setSelectedStartTime(`${startHour.toString().padStart(2, '0')}:00`);
                                              setSelectedEndTime(`${endHour.toString().padStart(2, '0')}:00`);
                                              // Set the matiere and open modal
                                              setSelectedMatiereForExam(matiere);
                                              setShowExamModal(true);
                                            }
                                          } catch (err) {
                                            console.error('Error parsing dropped matiere:', err);
                                          }
                                        }
                                      }}
                                      className={`h-12 border-b border-gray-100 transition-colors ${
                                        !isWeekend && canManage ? 'cursor-pointer' : ''
                                      } ${isInDragRange ? 'bg-purple-200' : !isWeekend && canManage ? 'hover:bg-purple-100/50' : ''}`}
                                    />
                                  );
                                })}

                                {/* Exams positioned by time */}
                                {day.exams.map((exam, i) => {
                                  const pos = getExamPosition(exam.startTime, exam.endTime);
                                  return (
                                    <div
                                      key={i}
                                      onClick={(e) => handleEditExam(exam, e)}
                                      className={`
                                        absolute left-0.5 right-0.5 rounded-md border shadow-sm overflow-hidden
                                        transition-all hover:shadow-md hover:z-10
                                        ${canManage ? 'cursor-pointer' : 'cursor-default'}
                                        ${exam.isRattrapage
                                          ? 'bg-amber-100 border-amber-300 hover:bg-amber-200'
                                          : 'bg-purple-100 border-purple-300 hover:bg-purple-200'}
                                      `}
                                      style={{ top: `${pos.top}px`, height: `${pos.height}px`, minHeight: '24px' }}
                                      title={`${exam.moduleElement.name}\n${exam.startTime} - ${exam.endTime}\n${exam.groupe.name}\nSalle: ${exam.room?.name || 'Non assignée'}\nSurveillant: ${exam.isExternalProctor ? 'Externe' : exam.professor ? `${exam.professor.firstName} ${exam.professor.lastName}` : 'Non assigné'}${canManage ? '\n\nCliquez pour modifier' : ''}`}
                                    >
                                      <div className="p-1 h-full flex flex-col gap-0.5">
                                        <div className="flex items-center gap-0.5 flex-shrink-0">
                                          <Clock className="h-2.5 w-2.5 text-gray-500 flex-shrink-0" />
                                          <span className="text-[9px] font-medium text-gray-600 truncate">
                                            {exam.startTime}-{exam.endTime}
                                          </span>
                                        </div>
                                        <p className={`text-[10px] font-semibold truncate leading-tight ${
                                          exam.isRattrapage ? 'text-amber-800' : 'text-purple-800'
                                        }`}>
                                          {exam.moduleElement.name}
                                        </p>
                                        {pos.height > 40 && (
                                          <>
                                            <p className="text-[9px] text-gray-600 truncate leading-tight">
                                              {exam.groupe.name}
                                            </p>
                                            {pos.height > 50 && exam.room && (
                                              <div className="flex items-center gap-0.5">
                                                <DoorOpen className="h-2 w-2 text-blue-600 flex-shrink-0" />
                                                <span className="text-[9px] text-blue-600 font-medium truncate">
                                                  {exam.room.name}
                                                </span>
                                              </div>
                                            )}
                                            {pos.height > 65 && (
                                              <div className="flex items-center gap-0.5">
                                                <GraduationCap className="h-2 w-2 text-gray-500 flex-shrink-0" />
                                                <span className="text-[9px] text-gray-600 truncate">
                                                  {exam.isExternalProctor
                                                    ? 'Surveillant externe'
                                                    : exam.professor
                                                    ? `${exam.professor.firstName} ${exam.professor.lastName}`
                                                    : 'Non assigné'}
                                                </span>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </main>
            </div>
          </div>
        )}

        {/* Progress Panel */}
        {showProgressPanel && selectedGroupeId && (
          <ExamSchedulingProgressPanel
            matieres={matieres}
            scheduledExamsMap={scheduledExamsMap}
            groupName={groupes.find(g => g.id === selectedGroupeId)?.name}
            semester={selectedSemester}
            examPeriods={examPeriods}
          />
        )}
      </div>

      {/* Exam Entry Modal */}
      {showExamModal && selectedAcademicYear && selectedCampusId && (
        <ExamEntryModal
          academicYearId={selectedAcademicYear.id}
          campusId={selectedCampusId}
          campusName={campuses.find((c) => c.id === selectedCampusId)?.name}
          campuses={campuses}
          filiereId={selectedOptionId || selectedFiliereId || undefined}
          groupeId={selectedGroupeId || undefined}
          niveau={selectedGroupe?.niveau || selectedNiveau || undefined}
          moduleElementId={selectedMatiereForExam?.id}
          selectedDate={selectedDate}
          selectedStartTime={selectedStartTime}
          selectedEndTime={selectedEndTime}
          defaultSemester={selectedSemester}
          isRattrapage={false}
          editMode={editMode}
          examToEdit={examToEdit}
          onClose={() => {
            setShowExamModal(false);
            setSelectedMatiereForExam(null);
            setSelectedStartTime(null);
            setSelectedEndTime(null);
            setEditMode(false);
            setExamToEdit(null);
          }}
          onSuccess={handleExamCreated}
          onDelete={handleExamDeleted}
        />
      )}
    </div>
  );
};

export default ExamSchedulingPage;
